import { useState } from 'react';
import { X } from 'lucide-react';
import { useUiLocale, infoLabel } from '../i18n/useUiLocale';
import { lookupInfo } from '../i18n/infoCatalog';
import { InfoParagraphs } from '../i18n/InfoParagraphs';

/**
 * Accessible modal: overlay click + Escape + Close button.
 * Use for on-demand help (Info) instead of long inline copy.
 */
export function InfoModal({ title, children, isOpen, onClose }) {
  const titleId = `info-modal-${String(title || 'help').replace(/\W+/g, '-').slice(0, 40)}`;
  const locale = useUiLocale();
  const closeLabel = locale === 'ko' ? '닫기' : 'Close';

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40"
      role="presentation"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-card rounded-lg shadow-xl max-w-lg w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 shrink-0">
          <h2 id={titleId} className="text-lg font-semibold text-gray-900 pr-2">
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-600"
            aria-label={closeLabel}
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="px-4 py-3 overflow-y-auto text-sm text-gray-700 space-y-3 leading-relaxed">
          {children}
        </div>
        <div className="border-t border-gray-200 px-4 py-3 flex justify-end shrink-0 bg-gray-50">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 text-sm font-medium"
          >
            {closeLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Opens Info modal. Label is always "Info".
 * If `title` (or `infoKey`) matches INFO_CATALOG, body and title follow the system language; otherwise the English children are shown.
 */
export function InfoButton({ title, children, className = '', label, infoKey }) {
  const [open, setOpen] = useState(false);
  const locale = useUiLocale();
  const key = infoKey || title;
  const localized = lookupInfo(key, locale);
  const displayTitle = localized?.title || title;
  const displayLabel = label || infoLabel();
  const body = localized?.paragraphs?.length ? (
    <InfoParagraphs paragraphs={localized.paragraphs} />
  ) : (
    children
  );

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen(true);
        }}
        className={`text-primary-600 font-medium hover:underline text-sm inline-flex items-center ${className}`}
      >
        {displayLabel}
      </button>
      <InfoModal title={displayTitle} isOpen={open} onClose={() => setOpen(false)}>
        {body}
      </InfoModal>
    </>
  );
}
